import { useRef, useState, type ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t border-border/70 py-6 first:border-t-0 first:pt-0">
      <h2 className="mb-4 font-sans text-[14px] font-semibold tracking-normal">{title}</h2>
      <div className="flex flex-col gap-3.5">{children}</div>
    </section>
  );
}

export function Row({ label, hint, error, children }: { label: string; hint?: ReactNode; error?: boolean; children: ReactNode }) {
  return (
    <div className="flex min-h-8 items-center gap-4">
      <div className="min-w-0 flex-1">
        <div className="text-[14px]">{label}</div>
        {hint && <div className={cn("text-[12.5px]", error ? "text-urgent" : "text-muted-foreground")}>{hint}</div>}
      </div>
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  );
}

export function NumberField({ value, min, max, onCommit, label, suffix }: { value: number; min: number; max: number; onCommit: (n: number) => void; label: string; suffix?: string }) {
  const [v, setV] = useState(String(value));
  const last = useRef(value);
  if (last.current !== value) {
    last.current = value;
    setV(String(value));
  }
  const commit = () => {
    const n = Math.round(Number(v));
    if (!Number.isFinite(n) || n < min || n > max) return setV(String(value));
    if (n !== value) onCommit(n);
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <Input aria-label={label} inputMode="numeric" className="w-16 text-right tabular" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && commit()} />
      {suffix && <span className="text-[13px] text-muted-foreground">{suffix}</span>}
    </span>
  );
}
